import React, { useEffect } from 'react';
import Select from 'react-select';
import { useFormContext, Controller, useWatch } from 'react-hook-form';
import PhoneInput from 'react-phone-number-input/react-hook-form-input';

import { Input, SeparatorLine, HelperText, InputSectionGroup } from '@components';
import { useThemeMode } from '@contexts/ThemeModeContext';
import usePanRooms from '@hooks/usePanRooms';
import { NumericDropdown } from '@types';
import { getReactSelectStyles } from '@utils';

import { IBuilding } from 'sdk-apogee';
import { Link } from 'react-router-dom';

type PersonalInformationProps = {
  hasMultipleSites?: boolean;
  isMobile?: boolean;
  buildingOptions: IBuilding[];
  roomBasedPanEnabled: boolean;
};

const PersonalInformation = React.forwardRef<HTMLElement, PersonalInformationProps>(
  function WrappedSection(
    { hasMultipleSites, isMobile, buildingOptions, roomBasedPanEnabled },
    ref,
  ) {
    const { resolvedTheme } = useThemeMode();
    const {
      register,
      formState: { errors },
      control,
      getValues,
      setError,
      setValue,
      clearErrors,
    } = useFormContext();

    const isValid = (input: string, isController: boolean = false): boolean =>
      isController ? !(errors as any)[input]?.value?.message : !(errors as any)[input]?.message;

    const selectedBuilding: NumericDropdown = useWatch({ control, name: 'building' });
    const selectStyles = React.useMemo(() => getReactSelectStyles(resolvedTheme), [resolvedTheme]);
    // @ts-ignore
    const selectedBuildingId = selectedBuilding?.id;
    // @ts-ignore
    const selectedBuildingLabel = selectedBuilding?.label;

    const { getPanRooms, panRooms, panRoomOptions } = usePanRooms({
      onError: () => {
        if (selectedBuildingId) {
          setValue('panRoom', '');
          setError('panRoom', {
            type: 'manual',
            // @ts-ignore Using JSX works, but typed as string.
            message: (
              <>
                We are having trouble finding rooms for the {selectedBuildingLabel} building. You
                can{' '}
                <button className="text-blue-500" onClick={() => fetchPanRooms(selectedBuilding)}>
                  try again
                </button>{' '}
                or select another building. Please{' '}
                <Link to={'/support'} className="text-blue-500">
                  contact support
                </Link>{' '}
                if the problem persists.
              </>
            ),
          });
        }
      },
    });

    const fetchPanRooms = (selectedBuilding: NumericDropdown) => {
      clearErrors('panRoom');
      getPanRooms(selectedBuilding?.id).then((response) => {
        if (selectedBuilding?.id && Array.isArray(response) && !response.length) {
          setValue('panRoom', '');
          setError('panRoom', {
            type: 'manual',
            // @ts-ignore Using JSX works, but typed as string.
            message: (
              <>
                {selectedBuilding?.name} doesn't have any rooms assigned. Select another building or{' '}
                <Link to={'/support'}>contact support</Link>.
              </>
            ),
          });
        } else if (selectedBuilding?.id) {
          setValue(
            'panRoom',
            response?.find(({ id }) => id === getValues('panRoom')?.id),
            { shouldValidate: true },
          );
        }
      });
    };

    useEffect(() => {
      if (roomBasedPanEnabled) {
        fetchPanRooms(selectedBuilding);
      }
    }, [selectedBuilding, roomBasedPanEnabled]);

    return (
      <section className="py-2 md:py-8 w-full" ref={ref}>
        <h2 className="text-sm md:text-xl font-bold">Personal Information</h2>
        <InputSectionGroup className="mt-4">
          <InputSectionGroup.Item>
            <div className="input-section-group__field">
              <label className="input-section-group__label" htmlFor="first-name">
                First Name
              </label>
              <div className="input-section-group__control">
                <Input
                  className="input-section-group__inline-input"
                  id="first-name"
                  noBorder
                  {...register('firstName')}
                  type="text"
                  {...(isValid('firstName') ? {} : { valid: false })}
                />
                {/* @ts-ignore */}
                <HelperText valid={isValid('firstName')}>{errors.firstName?.message}</HelperText>
              </div>
            </div>
          </InputSectionGroup.Item>

          <InputSectionGroup.Item>
            <div className="input-section-group__field">
              <label className="input-section-group__label" htmlFor="last-name">
                Last Name
              </label>
              <div className="input-section-group__control">
                <Input
                  className="input-section-group__inline-input"
                  id="last-name"
                  noBorder
                  {...register('lastName')}
                  type="text"
                  {...(isValid('lastName') ? {} : { valid: false })}
                />
                {/* @ts-ignore */}
                <HelperText valid={isValid('lastName')}>{errors.lastName?.message}</HelperText>
              </div>
            </div>
          </InputSectionGroup.Item>

          <InputSectionGroup.Item>
            <div className="input-section-group__field">
              <label className="input-section-group__label" htmlFor="email-address">
                Email Address
              </label>
              <div className="input-section-group__control">
                <Input
                  className="input-section-group__inline-input"
                  id="email-address"
                  noBorder
                  {...register('email')}
                  type="text"
                />
              </div>
            </div>
          </InputSectionGroup.Item>

          <InputSectionGroup.Item>
            <div className="input-section-group__field">
              <label className="input-section-group__label" htmlFor="phone">
                Cell Phone
              </label>
              <div className="input-section-group__control">
                <PhoneInput
                  className="input-section-group__inline-input"
                  control={control}
                  name="phone"
                  country="US"
                  inputComponent={Input}
                  placeholder="555-555-5555"
                  id="phone"
                  noBorder
                  {...(isValid('phone') ? {} : { valid: false })}
                />
                {/* @ts-ignore */}
                <HelperText valid={isValid('phone')}>{errors.phone?.message}</HelperText>
              </div>
            </div>
          </InputSectionGroup.Item>

          <InputSectionGroup.Item>
            <div className="input-section-group__field">
              <label className="input-section-group__label" htmlFor="building-select">
                Building
              </label>
              <div className="input-section-group__control">
                <Controller
                  name="building"
                  control={control}
                  render={({ field }) => (
                    <Select
                      {...field}
                      inputId="building-select"
                      isDisabled={hasMultipleSites}
                      placeholder="Select one"
                      styles={selectStyles}
                      options={buildingOptions.filter((b) => b.isActive)}
                      getOptionValue={({ id }) => id}
                      getOptionLabel={({ name }) => name}
                      components={{ IndicatorSeparator: () => null }}
                    />
                  )}
                />
                {hasMultipleSites && (
                  <HelperText>
                    Contact <Link to={'/support'}>Boldyn Networks Student Support</Link> to change
                    your building.
                  </HelperText>
                )}
                <HelperText valid={isValid('building', true) && isValid('building', false)}>
                  {/* @ts-ignore */}
                  {errors?.building?.value?.message || errors?.building?.message}
                </HelperText>
              </div>
            </div>
          </InputSectionGroup.Item>

          <InputSectionGroup.Item>
            <div className="input-section-group__field">
              <label className="input-section-group__label" htmlFor="room">
                Suite and/or Room #
              </label>
              <div className="input-section-group__control">
                {roomBasedPanEnabled ? (
                  <>
                    <Controller
                      name="panRoom"
                      control={control}
                      render={({ field }) => (
                        <Select
                          {...field}
                          inputId="room"
                          isDisabled={!panRoomOptions.length}
                          isLoading={panRooms.isPending}
                          styles={selectStyles}
                          placeholder={
                            !getValues('building') ? 'First Select A Building' : 'Select A Room'
                          }
                          noOptionsMessage={() => 'Room not found'}
                          options={panRooms?.data}
                          getOptionValue={({ id }) => id}
                          getOptionLabel={({ name }) => name}
                        />
                      )}
                    />
                    <HelperText valid={isValid('panRoom', true) && isValid('panRoom', false)}>
                      {/* @ts-ignore */}
                      {errors?.panRoom?.value?.message || errors?.panRoom?.message}
                    </HelperText>
                  </>
                ) : (
                  <>
                    <Input
                      className="input-section-group__inline-input"
                      id="room"
                      noBorder
                      {...register('room')}
                      placeholder="Enter your Suite/Room #"
                      type="text"
                      {...(isValid('room') ? {} : { valid: false })}
                    />
                    {/* @ts-ignore */}
                    <HelperText valid={isValid('room')}>{errors.room?.message}</HelperText>
                  </>
                )}
              </div>
            </div>
          </InputSectionGroup.Item>
        </InputSectionGroup>

        {isMobile && <SeparatorLine />}
      </section>
    );
  },
);

export default PersonalInformation;
